import React, { useState, useEffect, useRef } from 'react';
import { object } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withApollo } from 'react-apollo';
import { map, get, orderBy } from 'lodash';
import { Main } from 'base-components';
import { FlexContainer, NotFound, HasPermissions, Row, Column, Button, Icon, Text, Loading } from '../../common';
import MediaListener from '../../utils/MediaListener';
import { ANDROID_GREEN, ARGENT, WHITE } from '../../../utils/colors';
import MotivesDrawer from './MotivesDrawer';
import Header from './Header';
import ExternalFactorsTable from './ExternalFactorsTable';
import useExternalMotives from './useExternalMotives';
import { useExternalIssuesLogs } from './useExternalIssuesLogs';
import './external-factors.css';

const Tabs = [
    {
        key: 'activeMotives',
        text: 'Motivos activos'
    },
    {
        key: 'historyMotives',
        text: 'Historial de motivos'
    }
];

const ExternalFactors = ({ session, client }) => {
    const sessionSalesChannel = orderBy(map(get(session, 'salesChannels'), ({ name, store, id, kind }) => ({ label: `${store} - ${name}`, value: id, kind })), ['label'], ['asc']);
    const [tab, setTab] = useState('activeMotives');
    const [registerMotivesDrawer, setRegisterMotiveDrawer] = useState(false);
    const [selectedSalesChannels, setSelectedSalesChannels] = useState([]);
    const { motives, refetchMotives } = useExternalMotives(client);
    const [queryVariables, setQueryVariables] = useState({ size: 10, page: 1, filters: { isTurningOn: 1 } });
    const { issueLogs, refetchIssueLogs, loading } = useExternalIssuesLogs(client);
    const isFirstRender = useRef(true);

    useEffect(() => {
        refetchIssueLogs(queryVariables);
    }, [queryVariables]);

    useEffect(() => {
        if (!isFirstRender.current) {
            setQueryVariables({ ...queryVariables, filters: { isTurningOn: get(queryVariables, 'filters.isTurningOn') === 1 ? 0 : 1 } });
        } else {
            isFirstRender.current = false;
        }
    }, [tab]);

    const onClickTab = (type) => {
        if (tab === type) return null;

        return setTab(type);
    };

    const handleRegisterMotivesDrawer = () => {
        setRegisterMotiveDrawer(!registerMotivesDrawer);
    };

    return (
        <HasPermissions
            to={['room-management-page:view']}
            otherwise={<NotFound firstTextLine='Página no encontrada' />}
        >
            <Main background='f4f4f4' withFooter>
                <MediaListener values={['16.7%', '100%', '100%']}>
                    {({ currentValue }) => {
                        return (
                            <>
                                <FlexContainer justify='flex-end' paddingHorizontal={23} pT={15}>
                                    <Button onClick={handleRegisterMotivesDrawer} width={200}>
                                        <Row align='center' gap={7}>
                                            <Icon name='new-plus' size={15} color='#ffffff' />
                                            Agregar motivo
                                        </Row>
                                    </Button>
                                </FlexContainer>
                                <Header
                                    currentValue={currentValue}
                                    onChangeSalesChannels={({ value }) => {
                                        setSelectedSalesChannels(value);
                                    }}
                                    selectedSalesChannels={selectedSalesChannels}
                                    salesChannelsOptions={sessionSalesChannel}
                                    motives={motives}
                                    email={get(session, 'email')}
                                    onSearch={() => {
                                        const filters = {
                                            ...get(queryVariables, 'filters'), saleChannelId: map(selectedSalesChannels, 'value')
                                        };

                                        setQueryVariables({ ...queryVariables, page: 1, filters });
                                    }}
                                    refetchIssueLogs={() => refetchIssueLogs(queryVariables)}
                                />
                                {loading
                                    ? <Loading loading={loading} />
                                    : <FlexContainer direction='column' paddingHorizontal={23}>
                                        <Row backgroundColor={WHITE} mB={15} height={57}>
                                            {map(Tabs, ({ key, text }) => (
                                                <Column
                                                    padding={4}
                                                    height='100%'
                                                    align='center'
                                                    justify='center'
                                                    borderBottom={tab === key ? `3px solid ${ANDROID_GREEN}` : `1px solid ${ARGENT}`}
                                                    key={key}
                                                    onClick={() => onClickTab(key)}
                                                    pointer
                                                >
                                                    <Text text={text} strong />
                                                </Column>
                                            ))}
                                        </Row>
                                        {tab === 'activeMotives'
                                            ? <ExternalFactorsTable
                                                data={get(issueLogs, 'payload')}
                                                setCurrentPage={(value) => setQueryVariables({ ...queryVariables, page: value })}
                                                setPageSize={(value) => setQueryVariables({ ...queryVariables, size: value })}
                                                pageSize={get(queryVariables, 'size')}
                                                currentPage={get(queryVariables, 'page')}
                                                totalPages={get(issueLogs, 'totalCount')}
                                                sessionSalesChannel={sessionSalesChannel}
                                                tabActive={tab}
                                                email={get(session, 'email')}
                                                refetchIssueLogs={() => refetchIssueLogs(queryVariables)}
                                                motives={motives}
                                            />
                                            : <ExternalFactorsTable
                                                data={orderBy(get(issueLogs, 'payload'), 'turnedOffAt', 'desc')}
                                                setCurrentPage={(value) => setQueryVariables({ ...queryVariables, page: value })}
                                                setPageSize={(value) => setQueryVariables({ ...queryVariables, size: value })}
                                                pageSize={get(queryVariables, 'size')}
                                                currentPage={get(queryVariables, 'page')}
                                                totalPages={get(issueLogs, 'totalCount')}
                                                sessionSalesChannel={sessionSalesChannel}
                                                tabActive={tab}
                                                motives={motives}
                                            />
                                        }
                                    </FlexContainer>
                                }
                                <MotivesDrawer
                                    handleRegisterMotivesDrawer={handleRegisterMotivesDrawer}
                                    registerMotivesDrawer={registerMotivesDrawer}
                                    motives={motives}
                                    refetchMotives={refetchMotives}
                                />
                            </>
                        );
                    }}
                </MediaListener>
            </Main>
        </HasPermissions>
    );
};

ExternalFactors.propTypes = {
    session: object,
    client: object
};

export default withApollo(withRouter(ExternalFactors));
